"use client";

import React from "react";
import { ArrowUpRight, Calculator, CheckCircle2, Laptop } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function Kosztorysy() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    id: "kalkulacja",
    title: "Przejrzysta kalkulacja kosztów inwestycji",
    description: "Szczegółowy wykaz materiałów, robocizny i etapów realizacji ułatwiający podejmowanie decyzji budowlanych.",
    icon: Calculator
  }, {
    id: "budzet",
    title: "Pomoc w planowaniu budżetu",
    description: "Realne ramy finansowe przedsięwzięcia pozwalające uniknąć niespodziewanych wydatków w trakcie prac.",
    icon: CheckCircle2
  }, {
    id: "online",
    title: "Wygodnie online",
    description: "Szybki kontakt i sprawna wymiana dokumentacji projektowej przez dedykowany serwis kosztorysowy.",
    icon: Laptop
  }];
  return <section id="kosztorysy" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border/70 relative isolate overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-3xl text-center">
          {/* Architectural Datum Accent Notch & Label */}
          <div className="mx-auto mb-6 flex flex-wrap items-center justify-center gap-2.5">
            <span className="h-[2px] w-8 bg-accent" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
              Projekt kosztorysów
            </span>
            <span className="h-[2px] w-8 bg-accent" />
            <Badge variant="secondary" className="ml-1 text-[11px] font-mono uppercase bg-accent/20 text-accent-foreground border-accent/40 px-2 py-0.5">
              Nowość
            </Badge>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-sans">
            Precyzyjny kosztorys Twojej inwestycji
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans font-light">
            Poznaj pełną strukturę nakładów finansowych przed rozpoczęciem budowy lub remontu. Dedykowany serwis BUDSYS pozwala uzyskać przejrzyste zestawienie kosztów inwestycji i optymalnie zaplanować budżet.
          </p>
        </motion.div>

        {/* 3 Benefit Cards */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.id} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : index * 0.1,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border border-border bg-card/80 backdrop-blur-sm rounded-xl p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md flex flex-col justify-between">
                  <CardContent className="p-0">
                    <div className="size-11 rounded-lg bg-secondary border border-border flex items-center justify-center text-accent mb-5">
                      <Icon className="size-5 text-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold tracking-tight text-foreground font-sans">
                      {benefit.title}
                    </h3>
                    <p className="mt-2.5 text-sm text-muted-foreground font-sans font-light leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Primary Accent Action CTA */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: shouldReduceMotion ? 0 : 0.25
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 bg-accent text-accent-foreground hover:bg-accent/90 rounded-md font-semibold text-base transition-[background-color,box-shadow] shadow-md hover:shadow-lg">
            <Link href="https://kosztorys-budsys.pl/" newTab className="inline-flex items-center justify-center gap-2">
              <span>Przejdź do kosztorysów</span>
              <ArrowUpRight className="size-4 text-accent-foreground" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}